/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_WRAPPER
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Introduces a lightweight admin-only <button> wrapper that maps intent props
 * to the approved UAVTradeZone button tokens without pulling in Bootstrap.
 *
 * File Size Check:
 * - Current: 0
 * - Adding: ~82
 * - Projected: ~82
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium
 * - Tokens used: --font-size-body-small, --line-height-body-small, --font-family
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/scheduler/AdminExchangeRatesPage.tsx
 * - client/src/features/admin/users/AdminWorkingDataBox.tsx
 */

import React from "react";
import type { MouseEventHandler, ReactNode } from "react";

type Intent = "primary" | "secondary" | "danger" | "link";
type Size = "sm" | "md";

const intentClassMap: Record<Intent, string> = {
  primary: "btn-black",
  secondary: "btn-figma-1",
  danger: "btn-danger-figma",
  link: "link-more-info",
};

// [2026-01-07] IntentMap: keep intents tied directly to the approved button tokens.
const mergeClassNames = (...classes: Array<string | undefined | false | null>) =>
  classes.filter(Boolean).join(" ");

interface Props {
  label?: string;
  children?: ReactNode;
  intent?: Intent;
  size?: Size;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  title?: string;
}

const AdminButton = React.forwardRef<HTMLButtonElement, Props>(function AdminButton(
  {
  label,
  children,
  intent = "secondary",
  size = "md",
  type = "button",
  disabled,
  onClick,
  className,
  title,
  }: Props,
  ref,
) {
  // [2026-04-19] btn-figma-sm: Use design-system class instead of Bootstrap's btn-sm
  // to prevent Bootstrap's .btn-sm { border-radius: 0.25rem } from overriding the pill shape.
  const sizeClass = size === "sm" ? "btn-figma-sm" : undefined;
  const mergedClassName = mergeClassNames(intentClassMap[intent], sizeClass, className);

  // [2026-01-07] Content: allow a label plus extra nodes (icons, loaders) inside.
  return (
    <button
      type={type}
      ref={ref}
      className={mergedClassName}
      disabled={disabled}
      onClick={onClick}
      title={title}
    >
      {label && <span>{label}</span>}
      {children}
    </button>
  );
});

export default AdminButton;
